import type {
  AssumptionRef,
  CapabilityRequirement,
  EvidenceRef,
  GapRef,
  GovernedCapabilityIntent,
} from './governed-capability-manifest';
import type { GovernedToolOutputSection, StrategyAttachableFinding } from './governed-tool-output-envelope';
import type { AskHelixScenarioCaseDto, AskHelixScenarioRecommendationDto } from './plan-payload';
import type {
  DecisionAgentNumericMetadataContract,
  DecisionFamilyContract,
  ScenarioContract,
} from '../../decision-agent-platform';
import type { TradeoffScenarioOutputLike } from '../../tradeoff-engine';
import type { ValueCapacityMatrixPayloadV1 } from '../../ui-vision-patterns';

/**
 * The closed, reader-facing property-name surface shared by the decision-
 * modules banned-vocabulary declaration lint
 * (apps/api/src/decision-modules/__tests__/decision-modules-banned-vocabulary.extraction.ts)
 * and its spec (#5666 r3, replacing the r2 version that hand-typed the same
 * 21 names twice — once in the extraction module, once in the spec's
 * positive-control table — with nothing tying them together).
 *
 * Lives beside the contract types it draws from, and each entry is
 * `keyof`-checked against the REAL field on its owning type via the
 * `ReaderFacingFieldUnion` union below: renaming or removing one of these
 * fields on its contract type breaks `tsc` here, not just a comment.
 *
 * `summary` has no single owning DTO across the manifest/envelope/view-spec
 * contracts (it is written ad hoc into rules.ts monitoring payloads); cited
 * against `StrategyAttachableFinding.summary`, the one exported packages/lib
 * type that names it.
 */
type ReaderFacingFieldUnion =
  | keyof Pick<GovernedCapabilityIntent, 'label' | 'description' | 'examples'>
  | keyof Pick<CapabilityRequirement, 'label'>
  | keyof Pick<AssumptionRef, 'label'>
  | keyof Pick<GapRef, 'label'>
  | keyof Pick<EvidenceRef, 'claim'>
  | keyof Pick<GovernedToolOutputSection, 'title' | 'body' | 'narrationBodyTemplate'>
  | keyof Pick<StrategyAttachableFinding, 'summary'>
  | keyof Pick<AskHelixScenarioRecommendationDto, 'statement' | 'basis' | 'tradeoff'>
  | keyof Pick<AskHelixScenarioCaseDto, 'triggerLine'>
  | keyof Pick<DecisionFamilyContract, 'title' | 'executiveQuestion' | 'escalationLogic'>
  | keyof Pick<ScenarioContract, 'label' | 'objective'>
  | keyof Pick<TradeoffScenarioOutputLike, 'objective' | 'recommendation'>
  | keyof Pick<DecisionAgentNumericMetadataContract, 'definition' | 'reconciliationRule'>
  | keyof Pick<ValueCapacityMatrixPayloadV1, 'eyebrow' | 'xAxisLabel' | 'yAxisLabel'>;

/**
 * Every literal below must be a member of `ReaderFacingFieldUnion` (TS
 * rejects an entry that doesn't resolve to a real field on one of the cited
 * contract types) AND every union member must appear below (the
 * `AssertComplete` check two lines down rejects a union member missing from
 * the array) — so this list can neither drift ahead of the contracts nor
 * silently fall behind one either.
 */
export const READER_FACING_FIELDS = [
  'label',
  'title',
  'summary',
  'description',
  'narrationBodyTemplate',
  'definition',
  'objective',
  'recommendation',
  'statement',
  'basis',
  'tradeoff',
  'claim',
  'executiveQuestion',
  'examples',
  'eyebrow',
  'xAxisLabel',
  'yAxisLabel',
  'triggerLine',
  'escalationLogic',
  'body',
  'reconciliationRule',
] as const satisfies readonly ReaderFacingFieldUnion[];

// Compile-time completeness check, bidirectional: the `satisfies` clause
// above already rejects an array entry that is not a real field on one of
// the cited contract types; this rejects the OTHER direction -- a contract
// field named in `ReaderFacingFieldUnion` that got left out of the array.
// `AssertExact` resolves to `true` only when the two sides are exactly the
// same set; the `const` below fails to compile otherwise (a mismatch
// resolves to a tuple describing which names are missing, which is not
// assignable to `true`).
type AssertExact<Expected, Actual extends Expected> = Expected extends Actual
  ? true
  : ['reader-facing-fields.ts is missing these contract field names:', Exclude<Expected, Actual>];
const _completenessCheck: AssertExact<
  ReaderFacingFieldUnion,
  (typeof READER_FACING_FIELDS)[number]
> = true;
void _completenessCheck;
